import React from 'react';
import { Box, Chip, Divider } from '@drone-js/core';
import { connect } from 'react-redux';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardDateChip/OfferCardDateChip.selectors';
import { AvailabilitySchedule } from 'gql/autogen/AvailabilitySchedule';
import { dateChipGenerator, formatPriceWithAbbreviation } from 'component/page/detail/detailUtils';
import { parseISO } from 'date-fns';
import { getDateInYmdFormat, getFormattedDateWithTodayTomorrow } from 'core-framework/util/datetime/date';
import { ActivityAvailabilityStatus } from 'gql/autogen/ActivityAvailabilityStatus';
import { CmsProps } from 'core/type/BootstrapConfig.types';
import PopoverCalendar from 'component/page/detail/PopoverCalendar/PopoverCalendar';
import CalendarPriceInfoText from 'component/page/detail/PopoverCalendar/CalendarPriceInfoText';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesOfferDateChipClickEventActivitiesManifestV1,
    ActivitiesOfferDateChipSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import {
    ActivitiesOfferDateChipClickEventActivitiesFieldsV1,
    ActivitiesOfferDateChipSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import { killEvent } from 'core/utils/domUtils';
import { ActivityOfferToken } from 'core/type/Details.types';
import { useExperimentIsB } from 'component/common/Experiment/Experiment';
import { ExperimentId } from 'core/type/ExperimentId';

const DATE_CHIPS_SIZE = 3;

interface DateChipProps extends CmsProps {
    index: number;
    schedule: AvailabilitySchedule;
    selectedDateString: string;
    locale: string;
    selectedOfferId: number;
    onClick: (scheduleDate: Date, index: number) => void;
    showPriceLabel: boolean;
    activityId: number | undefined;
}

const getUpdatedSchedules = (dateChips: AvailabilitySchedule[], schedule: AvailabilitySchedule[]) => {
    return dateChips.map((dateChip) => {
        const newSchedule = schedule.find((x) => x.date === dateChip.date);
        if (newSchedule) {
            return newSchedule;
        }
        return dateChip;
    });
};

const OfferCardDateChip: React.FC<DateChipProps> = (props) => {
    const { schedule, showPriceLabel } = props;

    React.useEffect(() => {
        if (schedule.date) {
            trackElementEvent<ActivitiesOfferDateChipSeenEventActivitiesFieldsV1>(
                ActivitiesOfferDateChipSeenEventActivitiesManifestV1.ids,
                {},
                { offer_id: props.selectedOfferId, travel_date: schedule.date, action_element_index: props.index + 1, activity_id: props.activityId },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Track seen event should only run once
    }, []);

    if (!schedule.date) return null;

    const scheduleDate = parseISO(schedule.date);
    const dateLabel = getFormattedDateWithTodayTomorrow(scheduleDate, props.cms);
    const priceLabel = schedule.price ? formatPriceWithAbbreviation(schedule.price, props.locale) : '-';
    const isSelectedDate = props.selectedDateString === schedule.date;
    const isDisabled = schedule.status !== ActivityAvailabilityStatus.AVAILABLE;

    const onDateChipClick = (e: React.MouseEvent) => {
        killEvent(e);
        props.onClick(scheduleDate, props.index + 1);
    };

    return (
        <Box data-element-name="activities-offer-date-chip" data-testid="activities-offer-date-chip" flex={1}>
            <Chip
                size="m"
                label={dateLabel}
                caption={showPriceLabel ? priceLabel : ''}
                selection={isSelectedDate && !isDisabled ? 'selected' : 'unselected'}
                onClick={onDateChipClick}
                state={isDisabled ? 'disabled' : 'enabled'}
                width="parent-fit"
            />
        </Box>
    );
};
OfferCardDateChip.displayName = 'OfferCardDateChip';

const BookingOptionDateChipContainer: React.FC<Props> = (props) => {
    const { offerLevelSelectedDate, isScheduleReady, offerLevelSchedules, isShowCalendar } = props;
    const [dateChips, setDateChips] = React.useState<AvailabilitySchedule[]>([]);
    const selectedDateString = getDateInYmdFormat(offerLevelSelectedDate);
    const isHidePriceInCalendar = useExperimentIsB(ExperimentId.ACTD_5);

    React.useEffect(() => {
        if (!isScheduleReady) {
            return;
        }

        const updatedDateChips = getUpdatedSchedules(dateChips, offerLevelSchedules);
        const dateChipsSchedule = dateChipGenerator(DATE_CHIPS_SIZE, offerLevelSelectedDate, updatedDateChips, offerLevelSchedules);
        setDateChips(dateChipsSchedule);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isScheduleReady, offerLevelSelectedDate, offerLevelSchedules]);

    if (props.screenSize.tabletOrDesktop) {
        return null;
    }

    const onDateChipClick = (date: Date, index: number) => {
        trackElementEvent<ActivitiesOfferDateChipClickEventActivitiesFieldsV1>(
            ActivitiesOfferDateChipClickEventActivitiesManifestV1.ids,
            {},
            { offer_id: props.selectedOfferId, travel_date: getDateInYmdFormat(date), action_element_index: index, activity_id: props.activityId },
        );
        const offerToken: ActivityOfferToken = props.offerOptionToken
            ? { activityToken: props.offerOptionToken, enableOfferFilter: true }
            : { activityToken: props.activityTokenFromState, enableOfferFilter: false };

        props.setSelectedDate(offerToken, props.selectedOfferId, date);
    };

    const hidePricesInCalendar = props.behaviors.detail.hidePricesInCalendar || isHidePriceInCalendar;

    return (
        <>
            {isShowCalendar ? (
                <Box width="100%" pb="12">
                    <PopoverCalendar isOfferLevel={true} onSelectDateCallback={props.onCalendarClick} />
                </Box>
            ) : (
                <Box mb="24">
                    <Box display="flex" data-testid="activities-offer-date-chip-container" width="100%" justifyContent="space-between" gap="8" flexWrap="wrap">
                        {dateChips.map((schedule, index) => {
                            return (
                                // eslint-disable-next-line @agoda/agoda-a11y/no-non-drone-interactable -- @agoda/agoda-a11y/no-non-drone-interactable[37048ceb-cf4a-41a6-8e50-08faf72d5eb3]
                                <OfferCardDateChip
                                    key={index}
                                    index={index}
                                    cms={props.cms}
                                    locale={props.locale}
                                    schedule={schedule}
                                    selectedDateString={selectedDateString}
                                    selectedOfferId={props.selectedOfferId}
                                    showPriceLabel={!hidePricesInCalendar}
                                    onClick={onDateChipClick}
                                    activityId={props.activityId}
                                />
                            );
                        })}
                    </Box>
                    <Box mt="8">
                        <Divider my="16" />
                        <CalendarPriceInfoText />
                    </Box>
                </Box>
            )}
        </>
    );
};
BookingOptionDateChipContainer.displayName = 'BookingOptionDateChipContainer';

export default connect(mapStateToProps, mapDispatchToProps)(BookingOptionDateChipContainer);
